import dynamic from 'next/dynamic';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SxPropsTheme } from '../../styles/theme';
import { GradientTypography } from '../gradients/GradientTypography';
import { useTheme } from '@mui/material/styles';
import { Competition } from 'functions/src/types/firestore/Game/Competition';

const Countdown = dynamic(() => import('react-countdown'), {
  ssr: false,
});

function Counter({ label, counter }: { label: string; counter: number }) {
  return (
    <Stack justifyContent="center" alignItems="center">
      <Typography variant="h2">
        {counter.toString().padStart(2, '0')}
      </Typography>
      <GradientTypography gradientColor="primary.vertical" variant="h6">
        {label.toUpperCase()}
      </GradientTypography>
    </Stack>
  );
}

type EventTimeProps = Pick<Competition<Date>, 'date'> & {
  title?: string;
};

export function EventTime({ date, title = 'Time Remaining' }: EventTimeProps) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: 'min(586px, 100%)',
      }}
    >
      <GradientTypography
        gradientColor="primary.vertical"
        variant="h5"
        justifyContent={title === 'Time Remaining' ? 'flex-start' : 'center'}
        ml={title === 'Time Remaining' ? 3 : 0}
        sx={{
          mb: 1,
          ml: 3,
          textTransform: 'uppercase',
        }}
      >
        {title}
      </GradientTypography>
      <Stack
        flexDirection="row"
        justifyContent="space-evenly"
        alignItems="center"
        sx={{
          borderRadius: '10px',
          height: '164px',
          border: 'solid 2px transparent',
          backgroundImage: `linear-gradient(${theme.palette.background.elevation[10]}, 
            ${theme.palette.background.elevationSolid[10]}), 
            ${theme.palette.primary.vertical}`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'content-box, border-box',
          boxShadow: theme.shadows[4],
          '&:hover': {
            boxShadow: theme.shadows[7],
          },
        }}
      >
        <Countdown
          date={date}
          renderer={({ days, hours, minutes, seconds }) => {
            return (
              <>
                <Counter label="days" counter={days} />
                <Divider orientation="vertical" sx={DIVIDER_STYLES} />
                <Counter label="hours" counter={hours} />
                <Divider orientation="vertical" sx={DIVIDER_STYLES} />
                <Counter label="minutes" counter={minutes} />
                <Divider orientation="vertical" sx={DIVIDER_STYLES} />
                <Counter label="seconds" counter={seconds} />
              </>
            );
          }}
        />
      </Stack>
    </Box>
  );
}

const DIVIDER_STYLES: SxPropsTheme = {
  height: '50%',
  backgroundImage:
    'radial-gradient(circle, rgba(0, 100, 233, 0) 0%, #4DC8FF 0%, rgba(0, 100, 233, 0) 100%)',
  width: '1.25px',
};
